/**
 * Client-side ASR runtime asset cache (TT-825).
 *
 * Responsibilities:
 * - Register a narrowly-scoped service worker (`/asr-sw.js`) that serves only the
 *   hosted ASR asset path cache-first. The service worker is required because ONNX
 *   Runtime fetches the model and `ort-wasm-simd-threaded.jsep.*` files itself; a
 *   plain Cache API read cannot transparently satisfy those engine-internal fetches.
 * - Pre-cache the manifest's `expectedFiles` into the manifest's Cache Storage bucket
 *   (`manifest.cacheName`) so the model/runtime load from cache on subsequent visits
 *   and survive restarts.
 * - Validate the cached set before declaring local ASR ready; on failure keep the
 *   previous known-good cache and return false. The App loading gate (TT-825) stays
 *   closed and surfaces retry until assets are ready; runtime fallback to server
 *   placement is a separate concern (TT-831), not automatic on preload failure.
 * - Evict stale `asr-model-*` caches only after a new version validates, so the
 *   previous bucket survives until the new one is proven.
 *
 *   Whether that buys a free rollback depends on the manifest, and the two backends
 *   now differ. NeMo (`asrAssetManifest.js`) is still version-scoped
 *   (`asr-model-${ASR_ASSET_VERSION}`), so reverting its version reactivates the
 *   previous bucket with nothing to re-download. Vosk (`voskAssetManifest.js`) keys
 *   its bucket on `BUNDLE_CACHE_EPOCH`, deliberately NOT on the runtime version, so a
 *   runtime revert lands in the same bucket and re-fetches the ~0.9 MB of runtime
 *   entries that pruneStaleCacheEntries removed — the trade that stops every runtime
 *   bump from evicting the cached graph archive whose URL never moved. Read the
 *   trade-off note at that constant before assuming the old free-rollback property.
 *
 * This module is intentionally engine-agnostic: it manages assets and the service
 * worker only. The createAsr() call site (wiring ticket TT-828) consumes
 * getCreateAsrAssetOptions() so the engine resolves the same hosted URLs that were
 * pre-cached here.
 *
 * Known constraints baked in so future changes do not regress them:
 * - Fetches use `cache: 'no-store'` to avoid double-storing large assets in both the
 *   HTTP cache and Cache Storage.
 * - `ort.mjs` is cached here because TT-826 dynamically imports it from the same
 *   versioned CDN path as the ORT WASM files.
 * - SHA-256 integrity validation is opt-in (off by default) because hashing the
 *   ~26 MB model on every load is too expensive for low-end Chromebooks/iPads; the
 *   cheap `size_bytes` check runs by default when the companion manifest provides it.
 */

import { recordLocalAsrLifecycle, recordLocalAsrFailure, flushLocalAsrLifecycle } from './localAsrLifecycle';
import { ASR_ASSET_MANIFEST } from './asrAssetManifest';

/** Path of the service worker script, served from the app build root (scope `/`). */
const ASR_SERVICE_WORKER_URL = '/asr-sw.js';

/** Prefix used to identify ASR caches this module owns, for versioned eviction. */
const ASR_CACHE_PREFIX = 'asr-model-';

/** Metadata cache entry suffix used to persist response sizes without rereading blobs. */
const CACHE_METADATA_SUFFIX = '__asrCacheMetadata=size';

/** Maximum time to wait for the ASR service worker to activate before surfacing retry. */
const SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 10000;

const VALIDATION_STATUS = Object.freeze({
  READY: 'ready',
  RETRY: 'retry',
  PURGE: 'purge',
});

/**
 * Why a preload failed, reported to the caller through the `onOutcome` option so the
 * App loading gate can tell a deploy state apart from a broken download (TT-1198).
 *
 * `NOT_PUBLISHED` means every failed asset was one the CDN has no key for — it
 * answers those with the SPA shell (200 + HTML) or one of NOT_PUBLISHED_STATUSES.
 * Nothing is wrong with the device or the network, so blocking the student behind a
 * retry that cannot succeed is the wrong response; local ASR is simply unavailable
 * until the deploy finishes.
 *
 * Deliberately NOT reported for timeouts, 5xx, rate limits, network errors, or
 * validation failures: those are real problems the student can act on by retrying,
 * and they must keep reaching the retry UI.
 */
export const ASR_PRELOAD_FAILURE_REASON = Object.freeze({
  NOT_PUBLISHED: 'notPublished',
});

/**
 * Response statuses that mean the CDN has no key for the asset. Deliberately an
 * allowlist rather than "anything below 500": that range also carries transient
 * failures (429 rate limit, 408 request timeout) which must keep reaching the retry
 * UI instead of being reported as a deploy state.
 */
const NOT_PUBLISHED_STATUSES = new Set([403, 404, 410]);

/**
 * How long to keep waiting on the remaining downloads after the first "not published"
 * failure lands.
 *
 * Once any asset is definitively missing, validateCache cannot pass this session —
 * local ASR is already lost. The only thing still worth learning is whether something
 * ELSE fails differently, which would make the set unclassified and correctly route
 * the student to the retry UI. That question does not deserve the full
 * PRECACHE_OVERALL_TIMEOUT_MS: a large uncached archive on a throttled school link can
 * outlast it, and the overall timeout returns BEFORE classification runs, landing the
 * student back on the doomed retry modal this mechanism exists to avoid.
 *
 * Sized to cover a concurrent fetch failing its own way — a 5xx exhausts
 * FETCH_ATTEMPTS in ~1s of backoff (2 attempts, 1s base delay) — while staying far
 * below the App stall watchdog's 30s sampling window. A genuine failure that has not been ATTEMPTED yet when the window closes
 * is therefore reported as notPublished. That is the deliberate direction: the session
 * falls back to server ASR instead of blocking on a retry that cannot fix the missing
 * asset either way.
 */
const NOT_PUBLISHED_RESIDUAL_WAIT_MS = 5000;

/**
 * Asset-cache events that mean the preload is degraded: a validation failure, a missing
 * capability, or a fallback. Listed rather than derived from the complement so that an
 * event added later defaults to a milestone — the safe direction. The old allowlist ran
 * the other way and silently marked anything unlisted `failure: true`, which put the
 * severity of a log line several hundred lines from the call site that emits it.
 */
const ASSET_FAILURE_EVENTS = new Set([
  'unsupported',
  'fetchReturnedHtml',
  'poisonedCachePurged',
  'serviceWorkerActivationFailed',
  'serviceWorkerControllerUnavailable',
  'serviceWorkerRegisterError',
  'serviceWorkerRequiredButFailed',
  'drainProgressArrayBufferFailed',
  'sanitizeStoredResponseFailed',
  'validationMissingFile',
  'validationCompanionManifestMissing',
  'validationCompanionManifestInvalid',
  'validationSizeMismatch',
  'validationHashMismatch',
  'validationFailed',
  'overallTimeout',
  'postDownloadPhaseTimeout',
  'pruneStaleEntriesError',
  'evictStaleCachesError',
  'precacheDownloadFailed',
  'outcomeCallbackFailed',
  'precacheError',
]);

/**
 * Records one asset-cache event on the shared local ASR lifecycle timeline, so asset
 * work is ordered against the enablement, runtime-init, and graph-download events
 * instead of living in its own separate stream of S3 objects.
 * @param {object} detail Must carry `event`; remaining fields are recorded as-is.
 * @returns {void}
 */
function logAsrAssetEvent(detail = {}) {
  const { event, ...rest } = detail;
  const name = `assets.${event}`;
  if (ASSET_FAILURE_EVENTS.has(event)) {
    recordLocalAsrFailure(name, rest);
    return;
  }
  recordLocalAsrLifecycle(name, rest);
}

/**
 * Optional hook invoked before evicting a superseded cache version, allowing the
 * engine layer to dispose an active ONNX session so two large model/runtime
 * allocations are never resident at once during an upgrade. Registered by the wiring
 * ticket (TT-828); a no-op until then.
 * @type {null | (() => (void | Promise<void>))}
 */
let activeSessionDisposer = null;

/**
 * Registers a callback that disposes the currently-loaded ASR session. Called by the
 * cache layer immediately before deleting an old cache version during an upgrade.
 * @param {() => (void | Promise<void>)} disposer
 */
export function setActiveSessionDisposer(disposer) {
  activeSessionDisposer = typeof disposer === 'function' ? disposer : null;
}

/**
 * Reports whether the runtime supports the APIs the cache layer needs. When false,
 * the caller should skip pre-caching and use server-side placement.
 * @returns {boolean}
 */
export function isAsrCacheSupported() {
  return (
    typeof caches !== 'undefined' &&
    typeof fetch !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator
  );
}

/**
 * Bound on `navigator.storage.estimate()`. It queries the browser's quota
 * subsystem, which is usually fast but is not on any documented latency budget —
 * and this runs on the path that closes the loading gate, so it must never be
 * able to stall it.
 */
const STORAGE_ESTIMATE_TIMEOUT_MS = 1000;

/**
 * Asks the browser to exempt this origin from automatic eviction, and reports
 * whether it agreed. Chrome grants heuristically (site engagement, installed,
 * bookmarked) and can silently decline; WebKit effectively no-ops, and ITP's
 * 7-day cap on script-writable storage applies regardless of any grant. The
 * answer is therefore only knowable from the field — hence the return value.
 *
 * Fire this early (it is independent of the download) and hand the promise to
 * getStorageDiagnostics() at settle time. Never rejects.
 * @returns {Promise<boolean|null>} Grant result, or null when unsupported/errored.
 */
export function requestPersistentStorage() {
  if (
    typeof navigator === 'undefined' ||
    !navigator.storage ||
    typeof navigator.storage.persist !== 'function'
  ) {
    return Promise.resolve(null);
  }
  return navigator.storage.persist().then(
    (granted) => Boolean(granted),
    () => null,
  );
}

/**
 * Snapshot of this origin's storage state for telemetry: whether eviction
 * protection was granted, and how close the origin is to its quota. Purely
 * diagnostic — nothing branches on these values. Sampled at settle time so
 * `usage` reflects the post-download footprint (Cache Storage + IndexedDB, since
 * `estimate()` covers the whole origin bucket).
 *
 * Never rejects, and every step is bounded or guarded, so a browser with a slow
 * or broken quota subsystem degrades to null fields instead of delaying the
 * caller.
 * @param {Promise<boolean|null>} [persistedPromise] Result of requestPersistentStorage().
 * @returns {Promise<{storagePersisted: boolean|null, storageUsageBytes: number|null,
 *   storageQuotaBytes: number|null, storageUsageRatio: number|null}>}
 */
export async function getStorageDiagnostics(persistedPromise) {
  const unknown = {
    storagePersisted: null,
    storageUsageBytes: null,
    storageQuotaBytes: null,
    storageUsageRatio: null,
  };
  try {
    // Bounded like estimate() below: persist() can raise a permission prompt on
    // some browsers and stay pending until the user answers — this snapshot must
    // never inherit that wait.
    const storagePersisted = persistedPromise
      ? await Promise.race([
        Promise.resolve(persistedPromise).catch(() => null),
        delay(STORAGE_ESTIMATE_TIMEOUT_MS).then(() => null),
      ])
      : null;
    if (
      typeof navigator === 'undefined' ||
      !navigator.storage ||
      typeof navigator.storage.estimate !== 'function'
    ) {
      return { ...unknown, storagePersisted };
    }

    const estimate = await Promise.race([
      Promise.resolve(navigator.storage.estimate()).catch(() => null),
      delay(STORAGE_ESTIMATE_TIMEOUT_MS).then(() => null),
    ]);
    if (!estimate) {
      return { ...unknown, storagePersisted };
    }

    const usage = typeof estimate.usage === 'number' ? estimate.usage : null;
    const quota = typeof estimate.quota === 'number' ? estimate.quota : null;
    return {
      storagePersisted,
      storageUsageBytes: usage,
      storageQuotaBytes: quota,
      // Rounded to 4 dp: the interesting signal is "how close to the ceiling",
      // and full precision would just be noise in the logs.
      storageUsageRatio: usage !== null && quota ? Math.round((usage / quota) * 1e4) / 1e4 : null,
    };
  } catch {
    return unknown;
  }
}

/**
 * Resolves the path portion of a service worker script URL.
 * @param {string} scriptURL
 * @returns {string}
 */
function getServiceWorkerScriptPath(scriptURL) {
  return new URL(scriptURL).pathname;
}

/**
 * Resolves after a service worker reaches `activated`, or null if activation fails.
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
function waitForServiceWorkerActivation(registration) {
  if (!registration) {
    return Promise.resolve(null);
  }

  if (registration.active) {
    return Promise.resolve(registration);
  }

  const worker = registration.installing || registration.waiting;
  if (!worker) {
    return Promise.resolve(registration);
  }

  if (worker.state === 'activated') {
    return Promise.resolve(registration);
  }
  if (worker.state === 'redundant') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        worker.removeEventListener('statechange', onStateChange);
        resolve(null);
      }
    }, SERVICE_WORKER_ACTIVATION_TIMEOUT_MS);

    function settle(value) {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      worker.removeEventListener('statechange', onStateChange);
      resolve(value);
    }

    function onStateChange() {
      if (worker.state === 'activated') {
        settle(registration);
      } else if (worker.state === 'redundant') {
        settle(null);
      }
    }

    worker.addEventListener('statechange', onStateChange);
  });
}

/**
 * Waits briefly for the active service worker to claim the current page.
 * @returns {Promise<boolean>}
 */
function waitForServiceWorkerController() {
  if (navigator.serviceWorker.controller) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        resolve(Boolean(navigator.serviceWorker.controller));
      }
    }, 5000);

    function onControllerChange() {
      if (!settled) {
        settled = true;
        clearTimeout(timeoutId);
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        resolve(true);
      }
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
  });
}

/**
 * Audits existing service worker registrations and registers the ASR worker only if
 * it is not already controlling its scope. The worker is served from the app root so
 * no `Service-Worker-Allowed` header is required; its fetch handler self-restricts to
 * the ASR asset path, so an app-level worker added later is never replaced here.
 * @returns {Promise<ServiceWorkerRegistration | null>} The ASR registration, or null
 *   if registration is unsupported or fails. Caller treats null as preload not ready.
 */
export async function registerAsrServiceWorker() {
  if (!isAsrCacheSupported()) {
    return null;
  }

  const existing = await navigator.serviceWorker.getRegistrations();
  const alreadyRegistered = existing.find((reg) => {
    const active = reg.active || reg.installing || reg.waiting;
    return active && active.scriptURL && getServiceWorkerScriptPath(active.scriptURL) === ASR_SERVICE_WORKER_URL;
  });
  const registration = alreadyRegistered || await navigator.serviceWorker.register(ASR_SERVICE_WORKER_URL);

  if (existing.length > 0) {
    // Document any pre-existing app worker so the ASR worker is understood not to
    // replace it; both can coexist because the ASR worker only handles asset URLs.
    logAsrAssetEvent({
      event: 'existingServiceWorkers',
      scriptURLs: existing
        .map((reg) => (reg.active && reg.active.scriptURL) || null)
        .filter(Boolean),
    });
  }

  const activatedRegistration = await waitForServiceWorkerActivation(registration);
  if (!activatedRegistration) {
    logAsrAssetEvent({ event: 'serviceWorkerActivationFailed' });
    return null;
  }
  const controlled = await waitForServiceWorkerController();
  if (!controlled) {
    logAsrAssetEvent({ event: 'serviceWorkerControllerUnavailable' });
    // The worker is active but has not claimed this page yet. Cache Storage can
    // still validate a returning visitor's cached assets instead of forcing retry.
  }

  return registration;
}

/** Number of attempts (1 initial + retries) for each asset fetch. Reduced from 3
 *  to 2 (TT-1268): CDN assets either resolve quickly or the connection is dead;
 *  a third 15 s wait adds no value and leaves students staring at 0% progress. */
const FETCH_ATTEMPTS = 2;

/** Base backoff between retries; grows exponentially per attempt. */
const RETRY_BASE_DELAY_MS = 1000;

/** Per-attempt fetch (connect + headers) timeout; body streaming is bounded
 *  separately by DRAIN_STALL_TIMEOUT_MS once headers arrive. Exported so the
 *  App-level stall watchdog can defer to this timeout before declaring a stall
 *  (TT-1268). Reduced from 60 s to 15 s: CDN cache-hit TTFB is 15–45 ms at p50
 *  and cache-miss is 80–300 ms; even on a congested school network with a MITM
 *  content filter, headers that haven't arrived in 15 s are not coming. */
export const FETCH_TIMEOUT_MS = 15000;

/** If no bytes arrive within this window during body streaming, abort the read. */
const DRAIN_STALL_TIMEOUT_MS = 30000;

/**
 * TT-1301: brief window fetchAndStore waits, after a cache.put rejection, for the
 * progress-clone drain to settle before recording drainOutcome. A network death fails
 * the clone within milliseconds of the put, so this lets DRAIN_OUTCOME.ERROR land; a
 * device Cache Storage write fault leaves the healthy clone streaming for far longer,
 * so it stays unresolved past this window and honestly records DRAIN_OUTCOME.PENDING
 * (INDETERMINATE — see summarizeFailureStages; never read as positive proof of a
 * healthy transfer). Kept short and only spent on the (rare) failure path, so it never
 * delays a healthy load, and SHARED across one precache call (awaitDrainSettleGrace)
 * so N concurrent failures cost at most this once, not once each.
 */
const DRAIN_SETTLE_GRACE_MS = 500;

/**
 * TT-1301 (H2): how long cache.put may remain unsettled AFTER its body has fully drained
 * before it is treated as a wedged Cache Storage write (FAILURE_STAGE.CACHE_PUT_TIMEOUT)
 * and routed to the retry UI. Progress is driven by the drain clone, so once the drain
 * completes the bar reads ~100% and the App stall watchdog's `< 1` guard is disarmed —
 * without this a hung write freezes the student at 100% until the 5-min overall timeout.
 * The grace arms ONLY after the drain settles, so a slow-but-healthy large write (which
 * streams in lockstep with the drain) is never false-failed; it bounds only the storage
 * flush of already-received bytes, which is seconds even on weak devices, so a generous
 * ceiling here trades a few seconds of frozen 100% for never bouncing a healthy device.
 */
const CACHE_PUT_SETTLE_GRACE_MS = 10000;

/** Maximum wall-clock time for the DOWNLOAD phase of precacheAsrAssets (fetch + store
 *  every asset). The post-download validate/prune/evict tail is bounded separately by
 *  POST_DOWNLOAD_PHASE_TIMEOUT_MS, so the whole-flow worst case is this plus that tail
 *  (TT-1301). */
const PRECACHE_OVERALL_TIMEOUT_MS = 300000;

/**
 * TT-1301: maximum wall-clock the post-download validate/prune/evict tail may take
 * before the loading gate is released to the retry UI. Once download progress reaches
 * 100% the App stall watchdog's `< 1` guard permanently disables it, so a hung Cache
 * Storage read here (cache.match / cache.keys — a known WebKit/school-device failure
 * class) would otherwise strand the student on the loading screen forever, with no
 * retry and no telemetry. A single shared deadline caps the WHOLE tail (not each
 * phase). Sized generously for a slow-but-healthy default (size-check) validation;
 * SHA-256 verification (opt-in, off by default) reads the full model and may need a
 * larger bound, supplied via options.postDownloadTimeoutMs.
 *
 * Hazard when this fires on `phase: 'validate'` (single source for this note): the
 * timed-out validateCache is abandoned but keeps running against the same
 * manifest.cacheName, and readCompanionManifest deletes a malformed companion entry. If
 * a superseding retry has since declared that bucket READY, that zombie delete would
 * corrupt the live bucket — the same shared-bucket hazard TT-1267 guards on the
 * fetch/purge paths. Guarded by threading the attempt's `signal` (via options.signal)
 * into validateCache/readCompanionManifest: the superseding attempt aborts this
 * attempt's signal, so the destructive delete is skipped once superseded (covers a
 * slow, not just hung, zombie). Caveat: Cache.delete takes no signal, so a delete
 * already IN FLIGHT when supersession lands can still complete — the window is narrowed
 * to one Cache I/O op, not fully closed. The sibling hang — a cache.put wedged at ~100%
 * drained progress, which this deadline arms too late to catch — is bounded separately
 * during the download phase by CACHE_PUT_SETTLE_GRACE_MS (FAILURE_STAGE.CACHE_PUT_TIMEOUT).
 */
const POST_DOWNLOAD_PHASE_TIMEOUT_MS = 30000;

/** Maximum simultaneous ASR asset downloads. */
const FETCH_CONCURRENCY = 2;

/**
 * Failure stages tagged onto a thrown fetch/store error (TT-1301). Frozen so a typo at
 * a comparison site fails loudly instead of silently missing its bucket — the same
 * convention as VALIDATION_STATUS / ASR_PRELOAD_FAILURE_REASON.
 */
const FAILURE_STAGE = Object.freeze({
  FETCH: 'fetch',
  CACHE_PUT: 'cachePut',
  CACHE_PUT_METADATA: 'cachePutMetadata',
  // The body cache.put did not reject but never settled after the body fully drained —
  // a wedged Cache Storage write bounded by CACHE_PUT_SETTLE_GRACE_MS (TT-1301 H2),
  // distinct from CACHE_PUT so a hang is not conflated with a rejection.
  CACHE_PUT_TIMEOUT: 'cachePutTimeout',
});

/**
 * Terminal state of the progress-clone drain — the discriminator that separates a
 * device Cache Storage write fault from a mid-body network death when cache.put rejects
 * with an identical message (TT-1301). Frozen for the same typo-safety reason as
 * FAILURE_STAGE. See summarizeFailureStages for how each bucket must be read.
 */
const DRAIN_OUTCOME = Object.freeze({
  OK: 'ok',
  ERROR: 'error',
  PENDING: 'pending',
});

/** Sentinel resolved by raceWithTimeout when a phase exceeds its budget (TT-1301). */
const PHASE_TIMEOUT = Symbol('phaseTimeout');

/**
 * Resolves to PHASE_TIMEOUT when `promise` has not settled within `ms`; otherwise
 * passes the promise's own resolution through. The losing timer is always cleared, so
 * no dangling setTimeout survives. A hung Cache Storage op cannot be cancelled, so the
 * underlying promise stays pending and detached — the point is to release the caller,
 * not the operation (TT-1301). A non-positive `ms` still yields the sentinel on the
 * next tick, so a shared deadline that has already elapsed short-circuits cleanly.
 * @param {Promise<*>} promise
 * @param {number} ms
 * @returns {Promise<* | typeof PHASE_TIMEOUT>}
 */
function raceWithTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(PHASE_TIMEOUT), Math.max(0, ms));
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Resolves after `ms` milliseconds.
 * @param {number} ms
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Builds the error thrown when an external signal aborts a fetch (TT-1267). Every caller
 * throws this straight out of fetchWithRetry (the top-of-loop, post-backoff, and catch
 * sites all rethrow it), so the loop's `if (error.permanent)` branch never actually sees
 * it — the `permanent` tag is defensive belt-and-braces, not load-bearing. Classification
 * of an aborted attempt reads the live `signal.aborted` state (the single source of
 * truth), not any error tag, because an abort supersedes the whole attempt, not one fetch.
 * @param {string} url
 * @returns {Error}
 */
function createAbortError(url) {
  const error = new Error(`ASR asset fetch aborted: ${url}`);
  error.permanent = true;
  return error;
}

/**
 * Returns the Cache Storage metadata key associated with a runtime asset URL.
 * @param {string} url
 * @returns {string}
 */
function getCacheMetadataUrl(url) {
  return `${url}${url.includes('?') ? '&' : '?'}${CACHE_METADATA_SUFFIX}`;
}

/**
 * Whether a response is HTML — which no ASR asset ever is (they are JS, wasm,
 * gzip archives, and JSON only). The CDN's SPA config rewrites a missing-key
 * 403/404 into `200 OK` + index.html, so a NOT-YET-DEPLOYED asset arrives as a
 * well-formed success response that `response.ok` cannot distinguish from real
 * bytes. Left unchecked that poisons everything downstream: the HTML gets stored
 * in Cache Storage and the cache-first service worker replays it forever, even
 * after the CDN heals.
 * @param {Response} response
 * @returns {boolean}
 */
function isHtmlResponse(response) {
  try {
    const type = response && response.headers && typeof response.headers.get === 'function'
      ? response.headers.get('content-type')
      : null;
    return Boolean(type && /text\/html/i.test(type));
  } catch {
    return false;
  }
}

/**
 * Fetches a URL with `cache: 'no-store'`, retrying with exponential backoff on a
 * network error or 5xx response. Permanent 4xx responses fail fast because retries
 * cannot fix a bad asset path/deploy — and so does an HTML body (see
 * isHtmlResponse: the SPA fallback for an asset that is not published; retrying
 * cannot help and the bytes must never be stored).
 *
 * Failing fast and being CLASSIFIED are separate things. An HTML body and the
 * NOT_PUBLISHED_STATUSES subset carry `failureReason = NOT_PUBLISHED`, so
 * precacheAsrAssets can classify them without re-inspecting the response; the rest
 * of the sub-500 range fails just as fast but stays unclassified, because a
 * transient status such as 429 or 408 must not read as a deploy state.
 *
 * Throws once attempts are exhausted so the caller can fail the pre-cache and keep
 * the previous known-good cache.
 *
 * An external `signal` (TT-1267) cancels an in-flight fetch when a retry or abandon
 * supersedes this attempt: it never issues another `fetch` once aborted, and rethrows
 * a permanent error instead of retrying, so an abandoned attempt stops downloading
 * rather than racing the new one for the same asset.
 * @param {string} url
 * @param {number} [attempts]
 * @param {{timeoutMs?: number, signal?: AbortSignal,
 *   onFetchActivity?: () => void}} [options]
 * @returns {Promise<Response>}
 */
async function fetchWithRetry(url, attempts = FETCH_ATTEMPTS, options = {}) {
  let lastError = null;
  const timeoutMs = options.timeoutMs || FETCH_TIMEOUT_MS;
  const externalSignal = options.signal || null;
  const onFetchActivity = options.onFetchActivity || null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (externalSignal && externalSignal.aborted) {
      throw createAbortError(url);
    }
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
      // The backoff above can span a retry/abandon; re-check before spending a fetch.
      if (externalSignal && externalSignal.aborted) {
        throw createAbortError(url);
      }
    }
    // Signal the App that a fetch attempt is starting, so the app-level stall
    // watchdog knows the cache layer is alive even before headers arrive or
    // progress advances. Fires on every retry attempt (TT-1268).
    if (onFetchActivity) onFetchActivity();

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;
    // { once: true } so a fired listener removes itself: the per-attempt controller is
    // discarded each iteration, so unfired listeners are bounded (this call adds up to
    // FETCH_ATTEMPTS; the signal is shared across the concurrent files, so at most
    // files x FETCH_ATTEMPTS total) and all die with the short-lived external signal.
    // abort() is idempotent with the timeout.
    //
    // DO NOT remove this listener in the finally below. It is load-bearing PAST the
    // `return response` on success: for a 22.8MB archive the headers arrive in ms and the
    // body streams for minutes, so a real abort almost always lands AFTER this function
    // returns, while fetchAndStore is draining the body. This listener is the only thing
    // that then routes the external abort onto controller.signal to cancel that in-flight
    // body stream. Tearing it down in finally would silently reduce cancellation to the
    // header phase only — and every existing abort test would still pass (they abort
    // before headers resolve). The test asserting an external abort still flips the
    // fetch signal AFTER fetchWithRetry has returned the response pins this.
    if (externalSignal && controller) {
      externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(url, {
        cache: 'no-store',
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (response.ok && isHtmlResponse(response)) {
        // Rejected at the HEADERS, before any body is drained, so a missing asset
        // contributes ZERO download progress and never reaches Cache Storage.
        //
        // Progress alone cannot carry this to the App gate: precacheAsrAssets
        // credits approxBytes for every already-cached file and reports progress
        // BEFORE the first fetch, so any state where some files are obtainable and
        // others are not opens above 0. A graph-revision bump where the archive
        // lags its companion leaves AM + runtime + wasm cached and opens at ~0.94.
        // The reason is therefore tagged here and propagated through
        // precacheAsrAssets' `onOutcome`, which the gate trusts over the number
        // (TT-1198).
        logAsrAssetEvent({ event: 'fetchReturnedHtml', url });
        const poisoned = new Error(`ASR asset fetch returned HTML (SPA fallback for a missing asset): ${url}`);
        poisoned.permanent = true;
        poisoned.failureReason = ASR_PRELOAD_FAILURE_REASON.NOT_PUBLISHED;
        throw poisoned;
      }
      if (response.ok) {
        // Headers arrived — refresh the activity timestamp so the app-level stall
        // watchdog knows the fetch is alive during body streaming. Without this, the
        // only activity signal is the attempt-start ping, and a slow-first-byte
        // response whose headers arrive just under FETCH_TIMEOUT_MS leaves the
        // watchdog on the exact boundary with no margin (TT-1268).
        if (onFetchActivity) onFetchActivity();
        return response;
      }
      if (response.status < 500) {
        // The whole sub-500 range stays permanent — retrying cannot fix a bad asset
        // path — but only 404/403/410 are CLASSIFIED as "not published", the shape
        // that takes on a CDN configured without the SPA rewrite. The rest of the
        // range must stay unclassified: 429 and 408 in particular are transient, and
        // a rate-limit during a morning login spike would otherwise be silently
        // downgraded to server ASR with no retry offered, and logged as a deploy
        // state.
        const permanentError = new Error(`ASR asset fetch failed: ${url} (${response.status})`);
        permanentError.permanent = true;
        if (NOT_PUBLISHED_STATUSES.has(response.status)) {
          permanentError.failureReason = ASR_PRELOAD_FAILURE_REASON.NOT_PUBLISHED;
        }
        throw permanentError;
      }
      lastError = new Error(`ASR asset fetch failed: ${url} (${response.status})`);
    } catch (error) {
      lastError = error;
      // An external abort surfaces as the fetch rejecting on controller.abort(). It is
      // deliberate supersession, not a transient failure, so stop rather than retry.
      if (externalSignal && externalSignal.aborted) {
        throw createAbortError(url);
      }
      if (error && error.permanent) {
        throw error;
      }
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }
  throw lastError || new Error(`ASR asset fetch failed: ${url}`);
}

/**
 * Races a reader chunk against a stall timeout. Resets the timer on every chunk so
 * steady progress is never interrupted, but a fully stalled stream aborts promptly.
 * @param {ReadableStreamDefaultReader} reader
 * @returns {Promise<ReadableStreamReadResult>}
 */
function readWithStallTimeout(reader) {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reader.cancel().catch(() => {});
      reject(new Error('ASR body stream stalled'));
    }, DRAIN_STALL_TIMEOUT_MS);

    reader.read().then(
      (result) => { clearTimeout(timeoutId); resolve(result); },
      (error) => { clearTimeout(timeoutId); reject(error); },
    );
  });
}

/**
 * Reads a response body to report download progress and records the decoded byte size.
 * Aborts if no chunk arrives within DRAIN_STALL_TIMEOUT_MS to prevent hung streams
 * from blocking the entire download indefinitely.
 * @param {Response} response
 * @param {(deltaBytes: number) => void} [onBytes]
 * @returns {Promise<number | null>}
 */
async function drainForProgress(response, onBytes) {
  let totalBytes = 0;
  if (response.body && typeof response.body.getReader === 'function') {
    const reader = response.body.getReader();
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await readWithStallTimeout(reader);
      if (done) {
        break;
      }
      const length = value.length || value.byteLength || 0;
      totalBytes += length;
      if (onBytes) {
        onBytes(length);
      }
    }
    return totalBytes;
  }

  // TT-1301: a mid-transfer failure here must REJECT, not resolve null, so the drain
  // leg reads as a failure consistent with the getReader path above (which rejects via
  // readWithStallTimeout). Resolving null would let a network death on a browser
  // without ReadableStream be tagged drainOutcome:'ok' and misclassified as a storage
  // fault. The sole caller already tolerates a rejection (its drain promise catches
  // it), so this changes only the classification, not control flow.
  let buffer;
  try {
    buffer = await response.arrayBuffer();
  } catch (error) {
    // Log the message (TT-1301): the whole point of this telemetry is that we
    // could not tell these failures apart from the logs — now there is an error in hand.
    logAsrAssetEvent({
      event: 'drainProgressArrayBufferFailed',
      message: error && error.message ? error.message : String(error),
    });
    throw error;
  }
  if (!buffer) {
    logAsrAssetEvent({ event: 'drainProgressArrayBufferFailed' });
    return null;
  }
  totalBytes = buffer.byteLength;
  if (onBytes) {
    onBytes(totalBytes);
  }
  return totalBytes;
}

/**
 * Rewraps a response whose body fetch has already transparently decoded, stripping
 * the now-stale `Content-Encoding` / `Content-Length` headers before storage. The
 * cached body is DECODED bytes, so replaying the original headers through the
 * service worker hands consumers a self-contradictory response — spec-wise SW
 * replays skip HTTP content decoding so it mostly works, but the mismatch is
 * browser-behavior-dependent and `WebAssembly.instantiateStreaming(vosk.wasm.br)`
 * is the consumer that fails loudest. Responses without `Content-Encoding` (and
 * any environment lacking full Response support) pass through untouched.
 * @param {Response} response
 * @param {string} [url] For the failure log only.
 * @returns {Response}
 */
function sanitizeStoredResponse(response, url) {
  try {
    const source = response.headers;
    if (!source || typeof source.get !== 'function' || !source.get('content-encoding')) {
      return response;
    }
    const headers = {};
    if (typeof source.forEach === 'function') {
      source.forEach((value, key) => {
        const name = String(key).toLowerCase();
        if (name !== 'content-encoding' && name !== 'content-length') {
          headers[name] = value;
        }
      });
    } else if (source.get('content-type')) {
      // Minimal header interfaces (tests): keep the type instantiateStreaming needs.
      headers['content-type'] = source.get('content-type');
    }
    return new Response(response.body, {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
  } catch (error) {
    // Deliberate fallback: storing the pristine response is exactly the
    // pre-sanitizer behavior (spec-plausible on SW replay), while throwing here
    // would fail the whole precache over header hygiene. Logged because the
    // downstream symptom (instantiateStreaming rejecting a replayed .br entry)
    // would otherwise be undiagnosable in the field.
    logAsrAssetEvent({
      event: 'sanitizeStoredResponseFailed',
      url,
      message: error && error.message ? error.message : String(error),
    });
    return response;
  }
}

/**
 * Fetches a single asset (with retry) and stores it in the given cache bucket, with
 * encoding headers sanitized (see sanitizeStoredResponse) so the stored entry is
 * consistent for the service worker to replay. A clone is streamed concurrently for
 * progress and size metadata, avoiding a large post-download buffer before Cache
 * Storage starts consuming the response.
 *
 * Each asset is fetched and stored independently, so a failure here leaves every
 * already-stored file in the bucket intact: a later attempt only re-fetches the
 * missing one (see precacheAsrAssets crediting cached bytes up front). That
 * partial-cache preservation is deliberate and the reason the retry path (App.js)
 * must NOT respond to a `cachePut` failure by purging the whole bucket — a wholesale
 * caches.delete() would discard good bytes and force a full bucket re-download on
 * exactly the slow/flaky devices it is trying to help (TT-1301). Purge stays gated on
 * actual poisoning/validation evidence (see the poisonedCachePurged path and
 * VALIDATION_STATUS.PURGE), never a generic Cache.put network error.
 * @param {Cache} cache
 * @param {string} url
 * @param {(deltaBytes: number) => void} [onBytes] Called with each received chunk size.
 * @param {AbortSignal} [signal] Cancels the in-flight fetch when a retry/abandon
 *   supersedes this attempt (TT-1267). Aborting the fetch cancels the response body,
 *   which rejects the clone's stream feeding `cache.put`, so `cache.put` rejects
 *   without storing a partial entry — an aborted asset stays absent and is re-fetched
 *   cleanly by the superseding attempt.
 * @param {() => void} [onFetchActivity] Signals the caller that the cache layer is
 *   alive: attempt start, headers received (both via fetchWithRetry), and each body
 *   chunk (via the drain wrapper below). The app-level stall watchdog defers to this
 *   signal so it doesn't false-positive on slow-but-healthy downloads (TT-1268).
 *   Pinging per chunk is necessary because DRAIN_STALL_TIMEOUT_MS (30s) exceeds
 *   FETCH_TIMEOUT_MS (15s), so the gate must track body-phase liveness too.
 * @param {{deadline: number|null}} [graceState] Shared drain-settle grace budget for
 *   one precacheAsrAssets call (TT-1301). Defaults to a fresh per-call budget so
 *   direct callers/tests keep the original single-asset grace behavior.
 * @returns {Promise<void>}
 */
async function fetchAndStore(cache, url, onBytes, signal, onFetchActivity, graceState = { deadline: null }) {
  let response;
  try {
    response = await fetchWithRetry(url, FETCH_ATTEMPTS, { signal, onFetchActivity });
  } catch (error) {
    // TT-1301: tag the network/transfer surface so failure telemetry can tell a
    // transfer problem apart from a Cache Storage write fault. String-guarded
    // because assigning a property to a non-object rejection throws a TypeError in
    // strict-mode ESM, which would replace the original error.
    tagFailureStage(error, FAILURE_STAGE.FETCH);
    throw error;
  }
  const progressCopy = response.clone();

  // TT-1301: count bytes drained from the progress clone. Paired with drainOutcome
  // below, this is the transfer-health discriminator the failure telemetry needs:
  // cache.put rejects with the SAME "Cache.put() encountered a network error" both
  // when the device's Cache Storage write faults AND when the network dies mid-body
  // (cache.put consumes the live body stream). Bytes drained + whether the drain leg
  // itself errored is what separates the two.
  let bytesDrained = 0;
  const onBytesWithActivity = (delta) => {
    bytesDrained += delta;
    if (onFetchActivity) onFetchActivity();
    if (onBytes) onBytes(delta);
  };

  // Never rejects (preserving the prior `.catch(() => null)` contract) so the put/drain
  // race below sees a cache.put rejection unambiguously. The .then/.catch records the
  // drain's terminal state, which is the discriminator's positive signal:
  //   OK      => the transfer completed => the put failing is a storage write fault.
  //   ERROR   => the network died mid-body (the clone rejected) => transfer failure.
  //   PENDING => INDETERMINATE, not "healthy". Either the put rejected before the
  //              clone settled (fast storage fault) OR the stream went quiet and is
  //              still on its DRAIN_STALL_TIMEOUT_MS leash (a slow/stalled transfer).
  // TT-1313 must keep these three buckets distinct and treat PENDING as unknown.
  let drainOutcome = DRAIN_OUTCOME.PENDING;
  const drainPromise = drainForProgress(progressCopy, onBytesWithActivity).then(
    (size) => { drainOutcome = DRAIN_OUTCOME.OK; return size; },
    () => { drainOutcome = DRAIN_OUTCOME.ERROR; return null; },
  );

  const putStartedAt = Date.now();
  // Wrap cache.put so it never rejects — the outcome is inspected below. This is what
  // lets the drain-armed backstop leg race it without a late rejection orphaning when
  // the race settles the other way.
  const putResult = cache.put(url, sanitizeStoredResponse(response, url)).then(
    () => ({ ok: true }),
    (err) => ({ ok: false, err, failedAtMs: Date.now() }),
  );
  // TT-1301 (H2): a wedged Cache Storage WRITE can leave cache.put pending after the body
  // has fully drained — progress reads ~100% (it is driven by the drain, not the put), so
  // the App stall watchdog's `< 1` guard is already disarmed and nothing short of the
  // 5-min overall timeout bounds it. Arm a bounded put-settle grace, but ONLY after the
  // drain settles: while the drain is still streaming, the put is streaming the same bytes
  // and is healthy, so a slow-but-large write is never false-failed. On the getReader path
  // drainPromise always settles (it completes or its DRAIN_STALL_TIMEOUT_MS leash rejects),
  // so the backstop always arms; the arrayBuffer() fallback has no stall leash, but local
  // ASR is gated behind cross-origin isolation (localAsrEnablement) so production devices
  // reach the getReader path. Fail-fast on a genuine rejection is preserved — putResult
  // resolves {ok:false} immediately and wins the race before the grace.
  const drainThenGrace = drainPromise.then(
    () => raceWithTimeout(putResult, CACHE_PUT_SETTLE_GRACE_MS),
  );
  // NOTE: the put backstop cannot cancel a wedged Cache.put (it takes no signal), and by
  // the time it arms the body has fully drained, so abortActiveAsrPreload has no live
  // fetch to interrupt — the hung write's tee branch retains its bytes (~25 MB for the
  // ONNX/wasm/acoustic archives) until the browser reclaims it. Benign: a retry rewrites
  // identical immutable-per-URL bytes to the same URL (voskAssetManifest contract), so the
  // detached put cannot corrupt what the retry stores. Parallels the cache.delete zombie
  // note on POST_DOWNLOAD_PHASE_TIMEOUT_MS.
  const putOutcome = await Promise.race([putResult, drainThenGrace]);

  let sizeBytes;
  if (putOutcome === PHASE_TIMEOUT) {
    const timeoutError = new Error(`ASR cache.put stalled after drain: ${url}`);
    tagFailureStage(timeoutError, FAILURE_STAGE.CACHE_PUT_TIMEOUT, {
      stageDurationMs: Date.now() - putStartedAt,
      bytesDrained,
      drainOutcome,
      assetUrl: url,
    });
    throw timeoutError;
  }
  if (!putOutcome.ok) {
    const error = putOutcome.err;
    // stageDurationMs reflects the put's own failure latency, captured when it rejected
    // (before the settle grace below, which can add up to DRAIN_SETTLE_GRACE_MS).
    const stageDurationMs = putOutcome.failedAtMs - putStartedAt;
    // A fast put rejection means the drain's .then/.catch has usually NOT run yet and
    // drainOutcome would read a racy PENDING. The shared grace lets a network death
    // (clone rejects within ms) settle to ERROR before tagging.
    await awaitDrainSettleGrace(drainPromise, () => drainOutcome, signal, graceState);
    tagFailureStage(error, FAILURE_STAGE.CACHE_PUT, {
      stageDurationMs,
      bytesDrained,
      drainOutcome,
      assetUrl: url,
    });
    throw error;
  }
  // The put resolved; take the drained size (null when the drain errored, which skips
  // the metadata write below exactly as the prior Promise.all destructure did).
  sizeBytes = await drainPromise;

  if (typeof sizeBytes === 'number') {
    const metaStartedAt = Date.now();
    try {
      await cache.put(
        getCacheMetadataUrl(url),
        new Response(JSON.stringify({ sizeBytes }), {
          headers: { 'content-type': 'application/json' },
        }),
      );
    } catch (error) {
      // Separate stage (TT-1301): a quota/write reject on the tiny metadata entry must
      // not land in the main-body cachePut bucket and skew its denominator. The body
      // was already stored, so this is an UNAMBIGUOUS storage-write fault — carries its
      // own stageDurationMs and assetUrl (surfaced via summarizeFailureStages), but no
      // drain discriminator because none is needed here.
      tagFailureStage(error, FAILURE_STAGE.CACHE_PUT_METADATA, {
        stageDurationMs: Date.now() - metaStartedAt,
        assetUrl: url,
      });
      throw error;
    }
  }
}

/**
 * After a cache.put rejection, waits a brief bounded window for the progress-clone
 * drain to settle so a mid-body network death (whose clone rejects within ms) records
 * DRAIN_OUTCOME.ERROR instead of a racy PENDING, while a genuine storage fault (healthy
 * clone still streaming) stays PENDING/OK. Extracted from fetchAndStore so the race is
 * unit-testable in isolation (TT-1301).
 *
 * The grace budget is SHARED across one precacheAsrAssets call via `graceState`: the
 * first failure arms an absolute deadline and every later failure waits only until that
 * same deadline, so N failures cost at most DRAIN_SETTLE_GRACE_MS in total rather than
 * N x it (bounds the worst-case retry-UI delay under concurrent failures). A network
 * death settles its clone in milliseconds, so even a shrunken remaining window still
 * catches ERROR; once the deadline passes the outcome honestly stays PENDING
 * (indeterminate), never mislabeled healthy.
 *
 * No-ops when the drain already settled (drainOutcome leaves PENDING only while
 * unresolved) or the attempt was aborted (an abort short-circuits before this failure
 * is ever logged). The losing timer is always cleared.
 * @param {Promise<*>} drainPromise The never-rejecting progress-clone drain.
 * @param {() => string} getDrainOutcome Reads the live drain outcome.
 * @param {AbortSignal|null} signal
 * @param {{deadline: number|null}} graceState Shared per-precache grace budget.
 * @returns {Promise<void>}
 */
async function awaitDrainSettleGrace(drainPromise, getDrainOutcome, signal, graceState) {
  if (getDrainOutcome() !== DRAIN_OUTCOME.PENDING || (signal && signal.aborted)) {
    return;
  }
  const now = Date.now();
  if (graceState.deadline == null) {
    graceState.deadline = now + DRAIN_SETTLE_GRACE_MS;
  }
  const remainingMs = graceState.deadline - now;
  if (remainingMs <= 0) {
    return;
  }
  let graceTimer;
  await Promise.race([
    drainPromise,
    new Promise((resolve) => { graceTimer = setTimeout(resolve, remainingMs); }),
  ]);
  clearTimeout(graceTimer);
}

/**
 * Annotates a thrown fetch/store error with its failure stage and optional detail
 * for the TT-1301 failure telemetry, without clobbering an already-tagged error.
 * Guarded to object errors only: `error.failureStage = ...` on a string/primitive
 * rejection throws a TypeError in strict-mode ESM and would replace the original
 * error, turning a diagnosable network/storage failure into a spurious one.
 * Non-extensible errors (frozen/sealed) are skipped as well as primitives: assigning
 * to them also throws in strict mode, which would replace the original error — the
 * exact failure mode this guard exists to prevent (TT-1301).
 * @param {unknown} error The thrown value.
 * @param {string} stage A FAILURE_STAGE value.
 * @param {{stageDurationMs?: number, bytesDrained?: number, drainOutcome?: string,
 *   assetUrl?: string}} [detail]
 * @returns {void}
 */
function tagFailureStage(error, stage, detail = {}) {
  if (!error || typeof error !== 'object' || error.failureStage || !Object.isExtensible(error)) {
    return;
  }
  error.failureStage = stage;
  if (typeof detail.stageDurationMs === 'number') {
    error.stageDurationMs = detail.stageDurationMs;
  }
  if (typeof detail.bytesDrained === 'number') {
    error.bytesDrained = detail.bytesDrained;
  }
  if (detail.drainOutcome) {
    error.drainOutcome = detail.drainOutcome;
  }
  // assetUrl gives bytesDrained a denominator: the analyst joins it to the manifest's
  // approxBytes for that file to tell a stillborn transfer from a near-complete one
  // (TT-1301). Content-Length is the COMPRESSED size and would not match the
  // decoded bytesDrained, so it is deliberately not used here.
  if (detail.assetUrl) {
    error.assetUrl = detail.assetUrl;
  }
}

/**
 * The archive layers a manifest ships, each paired with its own companion
 * `.manifest.json`. Monolithic manifests (ORT) have exactly the
 * model pair; split-bundle manifests (vosk-v3+) add the decoding-graph layer.
 * Each layer validates against its OWN companion's `size_bytes`/`sha256` — the
 * pairing between an archive and its companion is per layer, so a bad pairing on
 * either one must fail that layer, not silently pass because the other looked fine.
 *
 * Only archives the precache actually WRITES are layers here: integrity validation
 * is a property of cached bytes, so an archive absent from `expectedFiles` has
 * nothing to size/sha-check in Cache Storage. That is the vosk acoustic archive
 * since TT-1313 A2 — the worker downloads it straight into IDBFS and its integrity
 * is the extraction failing loudly (createModel rejects -> server fallback). Its
 * companion stays in `expectedFiles` and is presence-checked by validateCache's
 * first loop as the deploy pre-flight; only the parse/self-heal step is skipped.
 * A manifest without `expectedFiles` (test doubles) keeps the pre-A2 behavior.
 * @param {typeof ASR_ASSET_MANIFEST} manifest
 * @returns {Array<{layer: string, archiveUrl: string, manifestUrl: string}>}
 */
function getArchiveLayers(manifest) {
  const isPrecached = (url) => !Array.isArray(manifest.expectedFiles)
    || manifest.expectedFiles.some((file) => file && file.url === url);
  const layers = [];
  if (manifest.modelUrl && manifest.modelManifestUrl && isPrecached(manifest.modelUrl)) {
    layers.push({ layer: 'model', archiveUrl: manifest.modelUrl, manifestUrl: manifest.modelManifestUrl });
  }
  if (manifest.graphUrl && manifest.graphManifestUrl && isPrecached(manifest.graphUrl)) {
    layers.push({ layer: 'graph', archiveUrl: manifest.graphUrl, manifestUrl: manifest.graphManifestUrl });
  }
  return layers;
}

/**
 * Confirms every `expectedFiles` entry is present in the cache. Optionally checks
 * each archive layer's byte size against its companion manifest's `size_bytes`
 * (cheap) and, when `verifyHashes` is true, its SHA-256 (expensive; intended for
 * QA, not every boot). A mismatch on ANY layer purges: the bucket re-downloads as
 * a unit, so a bad archive/companion pairing can never serve one stale layer.
 * @param {Cache} cache
 * @param {typeof ASR_ASSET_MANIFEST} manifest
 * @param {{verifyHashes?: boolean, signal?: AbortSignal}} [options] `signal` is
 *   read-only here (validateCache never aborts on it) and threaded to
 *   readCompanionManifest so a superseded pass skips its destructive delete — see the
 *   hazard note on POST_DOWNLOAD_PHASE_TIMEOUT_MS.
 * @returns {Promise<string>} Validation status describing whether retry or purge is needed.
 */
async function validateCache(cache, manifest, options = {}) {
  // Read off options rather than a separate positional arg so there is one channel for
  // the signal, matching the rest of the module's option-bag contract (TT-1301).
  const signal = options.signal || null;
  for (const { url } of manifest.expectedFiles) {
    // eslint-disable-next-line no-await-in-loop
    const match = await cache.match(url);
    if (!match) {
      logAsrAssetEvent({ event: 'validationMissingFile', url });
      return VALIDATION_STATUS.RETRY;
    }
  }

  for (const { layer, archiveUrl, manifestUrl } of getArchiveLayers(manifest)) {
    // eslint-disable-next-line no-await-in-loop
    const companion = await readCompanionManifest(cache, manifestUrl, layer, signal);
    if (!companion) {
      return VALIDATION_STATUS.RETRY;
    }
    // eslint-disable-next-line no-await-in-loop
    const layerValid = await validateModelIntegrity(cache, archiveUrl, companion, options, layer);
    if (!layerValid) {
      return VALIDATION_STATUS.PURGE;
    }
  }
  return VALIDATION_STATUS.READY;
}

/**
 * Reads and parses one cached companion `.manifest.json` metadata file. The
 * companion is part of `expectedFiles`, so this is intentionally a pure Cache
 * Storage read. If the small metadata file is malformed, only that entry is
 * removed so retry can refetch it without deleting the large cached
 * model/runtime files.
 * @param {Cache} cache
 * @param {string} manifestUrl The layer's companion manifest URL.
 * @param {string} [layer] Which archive layer this companion describes (log detail).
 * @param {AbortSignal|null} [signal] When aborted, the malformed-companion delete is
 *   skipped — see the zombie-validate hazard note on POST_DOWNLOAD_PHASE_TIMEOUT_MS.
 * @returns {Promise<object | null>}
 */
async function readCompanionManifest(cache, manifestUrl, layer, signal = null) {
  const match = await cache.match(manifestUrl);
  if (!match) {
    logAsrAssetEvent({ event: 'validationCompanionManifestMissing', layer });
    return null;
  }

  const parsed = await match.json().catch(() => null);
  if (!parsed) {
    logAsrAssetEvent({ event: 'validationCompanionManifestInvalid', layer });
    // Skip the self-heal delete when a superseding attempt has aborted this signal —
    // see the zombie-validate hazard note on POST_DOWNLOAD_PHASE_TIMEOUT_MS.
    if (!(signal && signal.aborted)) {
      await cache.delete(manifestUrl).catch(() => {});
    }
  }
  return parsed;
}

/**
 * Reads the stored decoded body size without loading a large response into memory.
 * @param {Cache} cache
 * @param {string} url
 * @returns {Promise<number | null>}
 */
async function readCachedBodySize(cache, url) {
  const metadata = await cache.match(getCacheMetadataUrl(url));
  if (metadata) {
    const parsed = await metadata.json().catch(() => null);
    if (parsed && typeof parsed.sizeBytes === 'number') {
      return parsed.sizeBytes;
    }
  }
  return null;
}

/**
 * Validates one cached archive layer against its companion manifest. Size is
 * always checked when present; SHA-256 is checked only when `verifyHashes` is set
 * and SubtleCrypto is available. The `validationSizeMismatch` line is the
 * documented signal for a bad archive/companion pairing — `layer` names which
 * pairing failed (`model` = acoustic/monolith archive, `graph` = split-bundle
 * decoding-graph archive).
 * @param {Cache} cache
 * @param {string} archiveUrl The layer's cached archive URL.
 * @param {object} companion The layer's own parsed companion manifest.
 * @param {{verifyHashes?: boolean}} options
 * @param {string} [layer] Which archive layer is being validated (log detail).
 * @returns {Promise<boolean>}
 */
async function validateModelIntegrity(cache, archiveUrl, companion, options, layer) {
  const modelMeta = companion && companion.model ? companion.model : null;
  if (!modelMeta) {
    return true;
  }

  const modelResponse = await cache.match(archiveUrl);
  if (!modelResponse) {
    return false;
  }

  let buffer = null;
  let bodySize = null;
  if (options.verifyHashes) {
    buffer = await modelResponse.clone().arrayBuffer();
    bodySize = buffer.byteLength;
  } else {
    bodySize = await readCachedBodySize(cache, archiveUrl);
  }

  if (typeof modelMeta.size_bytes === 'number') {
    if (typeof bodySize !== 'number') {
      // A skip, not a failure: validation continues and the cache still passes. Recorded
      // only so a later size question can be answered ("was the check even run?").
      logAsrAssetEvent({ event: 'validationSizeUnavailable', layer });
    } else if (bodySize !== modelMeta.size_bytes) {
      logAsrAssetEvent({
        event: 'validationSizeMismatch',
        expected: modelMeta.size_bytes,
        actual: bodySize,
        layer,
      });
      return false;
    }
  }

  if (options.verifyHashes && modelMeta.sha256 && typeof crypto !== 'undefined' && crypto.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    if (hex.toLowerCase() !== String(modelMeta.sha256).toLowerCase()) {
      logAsrAssetEvent({ event: 'validationHashMismatch', layer });
      return false;
    }
  }

  return true;
}

/**
 * Fetches files with a small concurrency limit to avoid saturating weak networks.
 * @param {Array<{url: string}>} files
 * @param {(file: {url: string}) => Promise<void>} worker
 * @returns {Promise<void>}
 */
async function runWithConcurrency(files, worker) {
  const queue = files.slice();
  const workers = Array.from({ length: Math.min(FETCH_CONCURRENCY, queue.length) }, async () => {
    while (queue.length > 0) {
      const file = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      await worker(file);
    }
  });
  await Promise.all(workers);
}

/**
 * Reduces a set of download failures to a single reported reason. Returns
 * NOT_PUBLISHED only when EVERY failure was one — a mix means something genuinely
 * broke alongside the missing asset, and a real connectivity problem must still
 * reach the retry UI rather than be silently downgraded to server ASR.
 * @param {Error[]} errors
 * @returns {string|null} An ASR_PRELOAD_FAILURE_REASON value, or null when unclassified.
 */
function classifyDownloadFailures(errors) {
  if (errors.length === 0) {
    return null;
  }
  const everyFailureIsMissingAsset = errors.every(
    (error) => error && error.failureReason === ASR_PRELOAD_FAILURE_REASON.NOT_PUBLISHED,
  );
  return everyFailureIsMissingAsset ? ASR_PRELOAD_FAILURE_REASON.NOT_PUBLISHED : null;
}

/**
 * Summarizes a set of download failures by stage for the TT-1301 failure telemetry.
 * This is the input the follow-up analysis uses to separate a device Cache Storage
 * fault from a transient transfer failure — the two are indistinguishable from the
 * error message or the `failureStage` tag alone, because a mid-body network death
 * rejects cache.put with the same message as a genuine storage write fault. The
 * discriminator is the transfer-health detail carried on each `cachePut` error,
 * captured after a brief settle grace (see DRAIN_SETTLE_GRACE_MS). Read the three
 * `drainOutcome` buckets as DISTINCT — do not collapse `'ok'` and `'pending'`:
 *   'ok'      => transfer completed, so the put failing is a storage write fault.
 *   'error'   => the network died mid-body => transfer failure.
 *   'pending' => INDETERMINATE (a fast storage fault OR a stalled/slow transfer the
 *                30s drain-stall leash has not yet failed). Not positive evidence
 *                either way; `bytesDrained` (joined to the asset's approxBytes via
 *                `assetUrl`) is the supporting signal for splitting this bucket.
 *
 * `stages` is one entry per failure (an untagged error defaults to 'unknown'), so its
 * length equals the failure total. Detail is bucketed by stage via an explicit
 * `failureStage` filter, so `cachePutFailures` carries ONLY body-`cachePut` errors and
 * `fastestCachePutFailureMs` is the minimum over exactly those — never a cross-stage
 * minimum. `cachePutMetadata` errors are surfaced separately in
 * `cachePutMetadataFailures` (they also carry `stageDurationMs`, and are an
 * unambiguous storage-write fault since the body already stored), NOT folded into the
 * `cachePut` bucket, so the transfer-health denominator there stays clean. Likewise a
 * body put that HUNG after full drain is surfaced in `cachePutTimeouts`, not in
 * `cachePutFailures` (its latency is grace-bound, not the put's own, so it must not skew
 * `fastestCachePutFailureMs`); a `cachePutTimeout` with `drainOutcome` `ok` (never
 * `pending` — the grace only arms after the drain settles, so the outcome is decided by
 * then) and a large `bytesDrained` is the clearest storage-write-hang signal for TT-1313.
 * Caveat: a
 * single error object rejected for two concurrent assets is tagged once but appears
 * once per array slot, so a *-Failures/*-Timeouts array can double-count its detail;
 * rare, and not deduped so `stages.length` stays truthful.
 * @param {Error[]} errors
 * @returns {{stages: string[], cachePutFailures: Array<{stageDurationMs: number|null,
 *   bytesDrained: number|null, drainOutcome: string|null, assetUrl: string|null}>,
 *   fastestCachePutFailureMs: number|null,
 *   cachePutMetadataFailures: Array<{stageDurationMs: number|null, assetUrl: string|null}>,
 *   cachePutTimeouts: Array<{bytesDrained: number|null, drainOutcome: string|null,
 *   assetUrl: string|null}>}}
 */
function summarizeFailureStages(errors) {
  const stages = errors.map((error) => (error && error.failureStage) || 'unknown');
  const cachePutFailures = errors
    .filter((error) => error && error.failureStage === FAILURE_STAGE.CACHE_PUT)
    .map((error) => ({
      stageDurationMs: typeof error.stageDurationMs === 'number' ? error.stageDurationMs : null,
      bytesDrained: typeof error.bytesDrained === 'number' ? error.bytesDrained : null,
      drainOutcome: error.drainOutcome || null,
      assetUrl: error.assetUrl || null,
    }));
  const durations = cachePutFailures
    .map((detail) => detail.stageDurationMs)
    .filter((ms) => typeof ms === 'number');
  const fastestCachePutFailureMs = durations.length ? Math.min(...durations) : null;
  const cachePutMetadataFailures = errors
    .filter((error) => error && error.failureStage === FAILURE_STAGE.CACHE_PUT_METADATA)
    .map((error) => ({
      stageDurationMs: typeof error.stageDurationMs === 'number' ? error.stageDurationMs : null,
      assetUrl: error.assetUrl || null,
    }));
  // A body put that hung after full drain (H2). Its latency is grace-bound, not the
  // put's own, so it is kept out of cachePutFailures/fastestCachePutFailureMs.
  const cachePutTimeouts = errors
    .filter((error) => error && error.failureStage === FAILURE_STAGE.CACHE_PUT_TIMEOUT)
    .map((error) => ({
      bytesDrained: typeof error.bytesDrained === 'number' ? error.bytesDrained : null,
      drainOutcome: error.drainOutcome || null,
      assetUrl: error.assetUrl || null,
    }));
  return {
    stages, cachePutFailures, fastestCachePutFailureMs, cachePutMetadataFailures, cachePutTimeouts,
  };
}

/**
 * Resolves a possibly-relative cached URL to absolute for set membership checks:
 * `cache.keys()` returns Requests with absolute URLs while manifest entries are
 * root-relative.
 * @param {string} url
 * @returns {string}
 */
function toAbsoluteCacheUrl(url) {
  try {
    const base = (typeof location !== 'undefined' && location.origin) || undefined;
    return new URL(url, base).href;
  } catch {
    return url;
  }
}

/**
 * Deletes entries in the ACTIVE cache bucket that are no longer in `expectedFiles`
 * (or their size-metadata companions). Bucket-level eviction only covers version
 * bumps; a URL swap within a version — e.g. adopting a precompressed `.br` sibling —
 * would otherwise strand the old entry in the active bucket forever. Runs only
 * after validation, and never fails the precache (best-effort cleanup).
 * @param {Cache} cache
 * @param {typeof ASR_ASSET_MANIFEST} manifest
 * @returns {Promise<void>}
 */
async function pruneStaleCacheEntries(cache, manifest) {
  if (typeof cache.keys !== 'function') {
    return;
  }
  const expected = new Set();
  manifest.expectedFiles.forEach(({ url }) => {
    expected.add(toAbsoluteCacheUrl(url));
    expected.add(toAbsoluteCacheUrl(getCacheMetadataUrl(url)));
  });

  const requests = await cache.keys();
  const stale = requests.filter((request) => {
    const url = typeof request === 'string' ? request : request.url;
    return !expected.has(toAbsoluteCacheUrl(url));
  });
  if (stale.length === 0) {
    return;
  }

  await Promise.all(stale.map((request) => cache.delete(request).catch(() => false)));
  logAsrAssetEvent({
    event: 'prunedStaleEntries',
    urls: stale.map((request) => (typeof request === 'string' ? request : request.url)),
  });
}

/**
 * Deletes superseded ASR caches (those matching the ASR prefix but not the active
 * cache name). Runs only after the active version has validated, so rollback by
 * manifest revert keeps a previous version intact until the replacement is proven.
 * @param {string} activeCacheName
 * @returns {Promise<void>}
 */
async function evictStaleCaches(activeCacheName) {
  const keys = await caches.keys();
  const stale = keys.filter((key) => key.startsWith(ASR_CACHE_PREFIX) && key !== activeCacheName);
  if (stale.length === 0) {
    return;
  }

  if (activeSessionDisposer) {
    await Promise.resolve(activeSessionDisposer()).catch(() => {});
  }

  await Promise.all(stale.map((key) => caches.delete(key)));
  logAsrAssetEvent({ event: 'evictedStaleCaches', caches: stale });
}

/**
 * Deletes every ASR model cache bucket owned by this module. Used by debug tooling
 * so testers can force a fresh download and exercise the loading progress UI.
 * @returns {Promise<string[]>} Cache names deleted; empty when unsupported or none found.
 */
export async function clearAsrAssetCaches() {
  if (!isAsrCacheSupported()) {
    return [];
  }

  const keys = await caches.keys();
  const owned = keys.filter((key) => key.startsWith(ASR_CACHE_PREFIX));
  if (owned.length === 0) {
    return [];
  }

  await Promise.all(owned.map((key) => caches.delete(key)));
  logAsrAssetEvent({ event: 'clearedAssetCaches', caches: owned });
  return owned;
}

/**
 * Pre-caches and validates the ASR runtime assets for a manifest version, fetching
 * with retry and reporting download progress. Never throws: any failure resolves to
 * `false` so callers can decide how to handle a failed download.
 *
 * Progress is weighted by each asset's `approxBytes` so the bar advances smoothly
 * across the two large files. Already-cached assets count as complete immediately, so
 * a returning visitor jumps to 100% without re-downloading. That credit is why
 * progress cannot double as a failure classifier and why `onOutcome` exists — see
 * ASR_PRELOAD_FAILURE_REASON (TT-1198).
 *
 * `onOutcome` mirrors the `onProgress` pattern rather than changing the return type:
 * the boolean is load-bearing at the App call site (`if (ready)`), and every object
 * is truthy, so returning one would make every failure read as success. It fires
 * exactly once per call that gets as far as opening the cache, on success and
 * failure alike.
 *
 * @param {typeof ASR_ASSET_MANIFEST} [manifest] Manifest to cache. Defaults to the
 *   active resolved manifest.
 * @param {{verifyHashes?: boolean, onProgress?: (fraction: number) => void,
 *   onOutcome?: (outcome: {reason: string|null}) => void,
 *   onFetchActivity?: () => void, overallTimeoutMs?: number,
 *   residualWaitMs?: number, postDownloadTimeoutMs?: number,
 *   signal?: AbortSignal}} [options] `residualWaitMs` overrides
 *   NOT_PUBLISHED_RESIDUAL_WAIT_MS; intended for tests. `postDownloadTimeoutMs` overrides
 *   POST_DOWNLOAD_PHASE_TIMEOUT_MS for the validate/prune/evict tail (read with `??`, so
 *   an explicit `0` is honored — an immediate tail timeout — not silently defaulted).
 *   `signal` cancels in-flight fetches when a retry/abandon supersedes this attempt
 *   (TT-1267); an aborted run resolves `false` and is logged as `precacheAborted`, never
 *   as a download failure.
 * @returns {Promise<boolean>} True when assets are cached and valid (local ASR ready).
 */
export async function precacheAsrAssets(manifest = ASR_ASSET_MANIFEST, options = {}) {
  if (!isAsrCacheSupported()) {
    logAsrAssetEvent({ event: 'unsupported' });
    return false;
  }

  const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
  const onOutcome = typeof options.onOutcome === 'function' ? options.onOutcome : null;
  const onFetchActivity = typeof options.onFetchActivity === 'function' ? options.onFetchActivity : null;
  const signal = options.signal || null;
  let outcomeReason = null;
  const totalBytes = manifest.expectedFiles.reduce((sum, file) => sum + (file.approxBytes || 0), 0) || 1;
  let receivedBytes = 0;
  const reportProgress = () => {
    if (onProgress) {
      onProgress(Math.min(receivedBytes / totalBytes, 1));
    }
  };

  const overallTimeoutMs = options.overallTimeoutMs || PRECACHE_OVERALL_TIMEOUT_MS;
  const startedAt = Date.now();
  let overallTimedOut = false;
  let overallTimeoutId = null;
  let residualWaitId = null;
  try {
    let cache = await caches.open(manifest.cacheName);

    let cachedState = await Promise.all(
      manifest.expectedFiles.map(async (file) => {
        const existing = await cache.match(file.url);
        return { file, existing };
      }),
    );
    // Self-heal a poisoned bucket: before the fetchReturnedHtml guard existed, a
    // preload run against a CDN whose asset was not yet deployed stored the SPA's
    // index.html under the asset's URL — and the cache-first service worker then
    // replays that HTML forever, even after the CDN heals.
    //
    // Purging the WHOLE bucket over one poisoned entry is a deliberate choice, not
    // a necessity: salvage would now be safe. Files are fetched independently, so a
    // partial pre-guard deploy stored real bytes for the published layers and HTML
    // only for the missing ones, and the runtime entries are designed to outlive graph
    // revisions — healthy runtime/wasm entries can sit beside a poisoned graph entry.
    // Deleting just the HTML entries (and their CACHE_METADATA_SUFFIX siblings, which
    // readCachedBodySize reads size from) would let per-layer validateCache catch
    // anything else stale. Wholesale is kept because this path fires ONLY on devices
    // poisoned by a pre-guard build — fetchWithRetry now refuses HTML, so no new
    // poisoning occurs and each affected device heals once — and because a bad heal
    // here is invisible and persistent (the SW replays it), which is worth being
    // conservative about. The re-download cost lands only where an AM was genuinely
    // cached, i.e. stage/dev2; prod never served these assets, so a prod bucket that
    // is poisoned at all is poisoned in every entry and loses nothing to the purge.
    const poisonedUrls = cachedState
      .filter(({ existing }) => existing && isHtmlResponse(existing))
      .map(({ file }) => file.url);
    if (poisonedUrls.length > 0) {
      // Same guard as the validation-purge below (TT-1267): an abort can land during the
      // caches.open / cache.match awaits above, and this caches.delete targets the version
      // bucket a superseding attempt may already be writing into. Bail before the
      // destructive delete so an abandoned run cannot wipe the live attempt's bytes.
      if (signal && signal.aborted) {
        logAsrAssetEvent({ event: 'precacheAborted', version: manifest.version });
        return false;
      }
      logAsrAssetEvent({ event: 'poisonedCachePurged', version: manifest.version, urls: poisonedUrls });
      await caches.delete(manifest.cacheName);
      cache = await caches.open(manifest.cacheName);
      cachedState = cachedState.map(({ file }) => ({ file, existing: null }));
    }
    cachedState.forEach(({ file, existing }) => {
      if (existing) {
        receivedBytes += file.approxBytes || 0;
      }
    });
    const missing = cachedState
      .filter(({ existing }) => !existing)
      .map(({ file }) => file);
    reportProgress();

    const timeoutPromise = new Promise((resolve) => {
      overallTimeoutId = setTimeout(() => {
        overallTimeoutId = null;
        overallTimedOut = true;
        logAsrAssetEvent({
          event: 'overallTimeout',
          elapsedMs: Date.now() - startedAt,
        });
        resolve();
      }, overallTimeoutMs);
    });

    // Bounded wait that starts at the first unpublished asset, so classification
    // cannot be lost to the overall timeout while a large archive finishes
    // downloading. See NOT_PUBLISHED_RESIDUAL_WAIT_MS.
    let resolveResidualWait = null;
    const residualWaitMs = options.residualWaitMs || NOT_PUBLISHED_RESIDUAL_WAIT_MS;
    const residualWaitPromise = new Promise((resolve) => { resolveResidualWait = resolve; });
    const startResidualWait = () => {
      if (residualWaitId) {
        return;
      }
      residualWaitId = setTimeout(() => {
        residualWaitId = null;
        logAsrAssetEvent({ event: 'notPublishedResidualWaitElapsed', waitedMs: residualWaitMs });
        resolveResidualWait();
      }, residualWaitMs);
    };

    // Failures are collected rather than rethrown so EVERY missing file gets
    // classified. Failing fast would report whichever failure landed first, and
    // one missing asset arriving ahead of a real network failure is exactly the
    // mix that must NOT be downgraded to a silent fallback. Waiting for the rest
    // costs no extra bandwidth — a rethrow only ended the await, the remaining
    // concurrency workers kept draining the queue regardless — and the residual
    // wait above bounds how long that costs the student.
    const downloadFailures = [];
    // Shared drain-settle grace budget for this whole precache call (TT-1301):
    // caps the total post-put settle wait at DRAIN_SETTLE_GRACE_MS even when several
    // assets fail, instead of paying it once per failing asset.
    const graceState = { deadline: null };
    const downloadWork = runWithConcurrency(missing, async (file) => {
      // Skipped, not recorded as a failure: the overall timeout is reported by the
      // race below, which returns before any classification runs. Collecting it here
      // would only build a list nothing reads. An external abort (TT-1267) skips for
      // the same reason — the abort short-circuit below returns before classification.
      if (overallTimedOut || (signal && signal.aborted)) {
        return;
      }
      try {
        await fetchAndStore(cache, file.url, (delta) => {
          receivedBytes += delta;
          reportProgress();
        }, signal, onFetchActivity, graceState);
      } catch (error) {
        downloadFailures.push(error);
        if (error && error.failureReason === ASR_PRELOAD_FAILURE_REASON.NOT_PUBLISHED) {
          startResidualWait();
        }
      }
    }).then(() => true);

    // No `.catch` needed on downloadWork despite the race: the worker above swallows
    // every rejection, so it cannot reject and orphan one when another leg wins. That
    // is load-bearing — a worker that rethrows must restore the no-op catch.
    //
    // The residual leg resolves TRUE so it falls through to classification below,
    // unlike the timeout leg, which is a genuine "we learned nothing" outcome.
    const downloadResult = await Promise.race([
      downloadWork,
      residualWaitPromise.then(() => true),
      timeoutPromise.then(() => false),
    ]);

    if (!downloadResult || overallTimedOut) {
      return false;
    }

    // TT-1301: the download race is decided, so stop the overall timer before the
    // post-download tail. Left running, its 5-min fire can log `overallTimeout` alongside
    // a successful `ready` and pollute the TT-1313 signal; the tail has its own bound
    // (POST_DOWNLOAD_PHASE_TIMEOUT_MS). Safe here — the guard above already returned on a
    // timeout that beat the download.
    if (overallTimeoutId) {
      clearTimeout(overallTimeoutId);
      overallTimeoutId = null;
    }

    // A retry/abandon superseded this attempt (TT-1267). The abandoned run's fetches
    // were cancelled, so its collected failures are AbortErrors, not real problems —
    // classify nothing and log a milestone rather than precacheDownloadFailed, so a
    // deliberate abort never reads as a network failure or a notPublished deploy state.
    if (signal && signal.aborted) {
      logAsrAssetEvent({ event: 'precacheAborted', version: manifest.version });
      return false;
    }

    if (downloadFailures.length > 0) {
      outcomeReason = classifyDownloadFailures(downloadFailures);
      logAsrAssetEvent({
        event: 'precacheDownloadFailed',
        version: manifest.version,
        failed: downloadFailures.length,
        reason: outcomeReason,
        // TT-1301: stage breakdown + per-cachePut transfer-health detail so the
        // follow-up analysis can separate a device Cache Storage fault from a
        // transient transfer failure.
        ...summarizeFailureStages(downloadFailures),
        messages: downloadFailures.map((error) => (error && error.message ? error.message : String(error))),
      });
      // TT-1313: sample usage/quota AT the failure site, not only at App's settle-time
      // snapshot — by then prune/evict may have moved the numbers, and "was the disk full
      // when Cache.put failed" is exactly the question. Its own event, NOT awaited: the
      // numbers are needed at the failure, not before the log line, and the student's
      // fallback path must not wait up to STORAGE_ESTIMATE_TIMEOUT_MS on the quota
      // subsystem. Never rejects; no persist() promise is passed so it cannot inherit a
      // pending permission prompt.
      getStorageDiagnostics(null).then((storage) => {
        logAsrAssetEvent({
          event: 'precacheDownloadFailedStorage',
          version: manifest.version,
          reason: outcomeReason,
          storageUsageBytes: storage.storageUsageBytes,
          storageQuotaBytes: storage.storageQuotaBytes,
          storageUsageRatio: storage.storageUsageRatio,
        });
        // Flush it, like boot.storageDiagnostics on the boot-failure path (App.js). This
        // resolves after the caller has already recorded preload.fallbackToServer and the
        // student is sitting on the retry modal, so the next lifecycle checkpoint may be far
        // off or never arrive — without a flush here the disk-full diagnostic this event
        // exists to capture can be lost (PR #6006 review #8a).
        flushLocalAsrLifecycle('precacheDownloadFailedStorage');
      });
      return false;
    }

    // TT-1301: bound the post-download tail. Past 100% progress the App stall
    // watchdog's `< 1` guard permanently disables it, so a hung Cache Storage read in
    // validate/prune/evict (a known WebKit/school-device failure class) would strand the
    // student on the loading screen forever. A single shared deadline caps the whole
    // tail — see POST_DOWNLOAD_PHASE_TIMEOUT_MS. On timeout the gate is released to the
    // retry UI rather than left waiting on a promise that may never settle.
    const postDownloadTimeoutMs = options.postDownloadTimeoutMs ?? POST_DOWNLOAD_PHASE_TIMEOUT_MS;
    const postDownloadDeadlineAt = Date.now() + postDownloadTimeoutMs;
    const remainingPostDownloadMs = () => postDownloadDeadlineAt - Date.now();

    const validationStatus = await raceWithTimeout(
      validateCache(cache, manifest, options),
      remainingPostDownloadMs(),
    );
    if (validationStatus === PHASE_TIMEOUT) {
      logAsrAssetEvent({ event: 'postDownloadPhaseTimeout', phase: 'validate', version: manifest.version });
      return false;
    }
    if (validationStatus !== VALIDATION_STATUS.READY) {
      // Re-check before the destructive purge (TT-1267): an abort can land during the
      // validateCache await above, and caches.delete targets the version bucket the
      // superseding attempt is ALREADY writing into. Without this guard, an abandoned run
      // that reached validation would wipe the live attempt's freshly stored bytes,
      // leaving it to fail validation against an empty bucket and bounce the student to
      // the retry UI. The check at line ~1235 cannot cover this — there is no await
      // between it and here, so it can only catch aborts that predate validation.
      if (signal && signal.aborted) {
        logAsrAssetEvent({ event: 'precacheAborted', version: manifest.version });
        return false;
      }
      if (validationStatus === VALIDATION_STATUS.PURGE) {
        await caches.delete(manifest.cacheName);
      }
      logAsrAssetEvent({
        event: 'validationFailed',
        version: manifest.version,
        status: validationStatus,
      });
      return false;
    }

    // Prune and evict are best-effort cleanup, so a timeout here logs and proceeds to
    // ready (the validated bucket is already usable) rather than failing the preload —
    // but they still share the post-download deadline so a hang in either cannot strand
    // the student before the ready gate opens.
    const prunePhase = await raceWithTimeout(
      pruneStaleCacheEntries(cache, manifest).catch((error) => {
        logAsrAssetEvent({
          event: 'pruneStaleEntriesError',
          message: error && error.message ? error.message : String(error),
        });
      }),
      remainingPostDownloadMs(),
    );
    if (prunePhase === PHASE_TIMEOUT) {
      logAsrAssetEvent({ event: 'postDownloadPhaseTimeout', phase: 'prune', version: manifest.version });
    }

    const evictPhase = await raceWithTimeout(
      // TT-1301: evict is best-effort cleanup like prune, so a genuine rejection (quota,
      // flaky storage) must be swallowed here. Without this .catch it escapes to the
      // outer catch and fails an otherwise-successful preload AFTER downloads and
      // validation both passed — contradicting the best-effort contract, which only held
      // for the timeout branch.
      evictStaleCaches(manifest.cacheName).catch((error) => {
        logAsrAssetEvent({
          event: 'evictStaleCachesError',
          message: error && error.message ? error.message : String(error),
        });
      }),
      remainingPostDownloadMs(),
    );
    if (evictPhase === PHASE_TIMEOUT) {
      logAsrAssetEvent({ event: 'postDownloadPhaseTimeout', phase: 'evict', version: manifest.version });
    }

    if (onProgress) {
      onProgress(1);
    }
    logAsrAssetEvent({
      event: 'ready',
      version: manifest.version,
      fetched: missing.length,
      elapsedMs: Date.now() - startedAt,
    });
    return true;
  } catch (error) {
    if (overallTimedOut) {
      return false;
    }
    logAsrAssetEvent({
      event: 'precacheError',
      message: error && error.message ? error.message : String(error),
    });
    return false;
  } finally {
    if (overallTimeoutId) {
      clearTimeout(overallTimeoutId);
    }
    if (residualWaitId) {
      clearTimeout(residualWaitId);
    }
    // Emitted from `finally` so the many early returns above cannot each forget it,
    // and so it is reported exactly once regardless of which one ran.
    //
    // Guarded because a throw from `finally` REPLACES the pending return value and
    // escapes the catch above it, which would turn this function's "never throws,
    // resolves false" contract into a rejection that ensureAsrAssetsReady passes
    // straight through. onProgress needs no such guard — it is called inside the try,
    // so a throw there still lands in the catch and still returns false.
    if (onOutcome) {
      try {
        onOutcome({ reason: outcomeReason });
      } catch (error) {
        logAsrAssetEvent({
          event: 'outcomeCallbackFailed',
          message: error && error.message ? error.message : String(error),
        });
      }
    }
  }
}

/**
 * High-level readiness entry point used by the app's loading gate. Registers the
 * service worker and pre-caches assets. Resolves to `false` (never rejects) when the
 * environment is unsupported or anything fails; App.js keeps the ready gate closed and
 * shows retry UI until a later call succeeds — unless `onOutcome` reports a reason
 * that says otherwise (see ASR_PRELOAD_FAILURE_REASON).
 *
 * The whole options bag is forwarded to precacheAsrAssets (including `overallTimeoutMs`,
 * `residualWaitMs`, and `postDownloadTimeoutMs`), so `onOutcome` reaches the App call
 * site unchanged. It is NOT invoked on the early returns owned by this function
 * (unsupported environment, signal aborted before or after service worker registration,
 * service worker registration failure) — none reaches the download, and all leave
 * progress at 0.
 *
 * @param {typeof ASR_ASSET_MANIFEST} [manifest]
 * @param {{verifyHashes?: boolean, onProgress?: (fraction: number) => void,
 *   onOutcome?: (outcome: {reason: string|null}) => void,
 *   onFetchActivity?: () => void, overallTimeoutMs?: number, residualWaitMs?: number,
 *   postDownloadTimeoutMs?: number, signal?: AbortSignal}} [options]
 * @returns {Promise<boolean>}
 */
export async function ensureAsrAssetsReady(manifest = ASR_ASSET_MANIFEST, options = {}) {
  if (!isAsrCacheSupported()) {
    return false;
  }
  // Skip the ~1s service worker registration when the caller already aborted (TT-1267):
  // a retry that superseded this attempt before it began has nothing to set up. Logged
  // (not silent) like every other early return here, and as the same precacheAborted
  // milestone precacheAsrAssets emits, so an abort reads consistently wherever it lands.
  if (options.signal && options.signal.aborted) {
    logAsrAssetEvent({ event: 'precacheAborted', version: manifest && manifest.version });
    return false;
  }
  const registration = await registerAsrServiceWorker().catch((error) => {
    logAsrAssetEvent({
      event: 'serviceWorkerRegisterError',
      message: error && error.message ? error.message : String(error),
    });
    return null;
  });
  if (!registration) {
    logAsrAssetEvent({ event: 'serviceWorkerRequiredButFailed' });
    return false;
  }
  // Re-check after registration (TT-1267): registerAsrServiceWorker is the ~1s,
  // non-cancellable await the entry bail above exists to skip, so an abort that lands
  // DURING it would otherwise fall through into precacheAsrAssets (opening the cache and
  // possibly running the poisoned purge) before the download-race check catches it. This
  // closes the same window the entry check opened for, at the same cost.
  if (options.signal && options.signal.aborted) {
    logAsrAssetEvent({ event: 'precacheAborted', version: manifest && manifest.version });
    return false;
  }
  return precacheAsrAssets(manifest, options);
}

export const __ASR_ASSET_CACHE_TESTS__ = {
  awaitDrainSettleGrace,
  drainForProgress,
  fetchAndStore,
  fetchWithRetry,
  getArchiveLayers,
  getCacheMetadataUrl,
  isHtmlResponse,
  summarizeFailureStages,
  tagFailureStage,
  pruneStaleCacheEntries,
  sanitizeStoredResponse,
  readWithStallTimeout,
  getServiceWorkerScriptPath,
  readCachedBodySize,
  readCompanionManifest,
  validateCache,
  validateModelIntegrity,
  waitForServiceWorkerActivation,
  waitForServiceWorkerController,
};
